import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import useAuthFetch from "../../../../hooks/useAuthFetch";
import useDebouncedSearchInput from "../../../../hooks/useDebouncedSearch";
import { BiSearch } from "react-icons/bi";
import { IoIosArrowDown } from "react-icons/io";

export const initialState = {
  page: 0,
  size: 10,
  sortBy: "createdAt",
  sortDirection: "DESC",
  searchPattern: "",
  status: "active",
  userId: "",
  city: "",
  tag: [],
  category: -1,
  type: -1,
  onlyWithReports: false,
  countryCode: "",
};

const SearchPanel = ({ searchSetting, setSearchSetting }) => {
  const [searchInput, setSearchInput] = useState("");
  const [createdBy, setCreatedBy] = useState("");
  const authFetch = useAuthFetch();

  const { data: tags, isLoading: tagsLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => authFetch("/v1/tags"),
    staleTime: 5 * 60 * 1000,
  });
  const { data: cities, isLoading: citiesLoading } = useQuery({
    queryKey: ["cities"],
    queryFn: () => authFetch("/v1/cities"),
    staleTime: 5 * 60 * 1000,
  });
  const { data: categories, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories"],
    queryFn: () => authFetch("/v1/categories"),
    staleTime: 5 * 60 * 1000,
  });
  const { data: types, isLoading: typesLoading } = useQuery({
    queryKey: ["types"],
    queryFn: () => authFetch("/v1/types"),
    staleTime: 5 * 60 * 1000,
  });
  const { data: countries, isLoading: countryLoading } = useQuery({
    queryKey: ["countries"],
    queryFn: () => authFetch("/v2/countries/places?pageSize=100"),
    staleTime: 5 * 60 * 1000,
  });

  useDebouncedSearchInput(searchInput, setSearchSetting, "searchPattern");
  useDebouncedSearchInput(createdBy, setSearchSetting, "userId");

  const handleSearchSettingsChange = (event) => {
    const { name, value } = event.target;
    setSearchSetting((prevEntity) => ({
      ...prevEntity,
      [name]: value,
      page: 0,
    }));
  };

  return (
    <div>
      {countryLoading ||
      citiesLoading ||
      tagsLoading ||
      categoriesLoading ||
      typesLoading ? (
        <span>Loading...</span>
      ) : (
        <div className="grid gap-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <BiSearch className="text-black" />
            </div>
            <input
              type="search"
              onChange={(event) => setSearchInput(event.target.value)}
              className="block w-full p-2 pl-10 text-sm text-gray-900 border border-gray-400 focus:outline-none"
              placeholder="Search place..."
            />
          </div>
          <div className="grid justify-start grid-cols-1 xl:grid-cols-4 lg:grid-cols-2 gap-x-8 gap-2">
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Country
                <select
                  name="countries"
                  value={searchSetting.countryCode}
                  defaultValue={searchSetting.countryCode}
                  onChange={(event) => {
                    setSearchSetting((prevState) => ({
                      ...prevState,
                      countryCode: event.target.value,
                    }));
                  }}
                  className="custom-select"
                >
                  <option value="">All</option>
                  {countries.data.map((country, i) => (
                    <option key={i} value={country.code}>
                      {country.name}
                    </option>
                  ))}
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                City
                <select
                  name="city"
                  value={searchSetting.city}
                  defaultValue={searchSetting.city}
                  onChange={(event) => {
                    setSearchSetting((prevState) => ({
                      ...prevState,
                      city: event.target.value,
                    }));
                  }}
                  className="custom-select"
                >
                  <option value="">All</option>
                  {cities.map((city, i) => (
                    <option key={i} value={city.name}>
                      {city.name}
                    </option>
                  ))}
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Tag
                <select
                  name="tag"
                  value={searchSetting.tag}
                  defaultValue={searchSetting.tag}
                  onChange={(event) => {
                    setSearchSetting((prevState) => ({
                      ...prevState,
                      tag: event.target.value,
                    }));
                  }}
                  className="custom-select"
                >
                  <option value="">All</option>
                  {tags.map((tag, i) => (
                    <option key={i} value={tag.id}>
                      {tag.name}
                    </option>
                  ))}
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Status
                <select
                  name="status"
                  value={searchSetting.status}
                  defaultValue={searchSetting.status}
                  onChange={handleSearchSettingsChange}
                  className="custom-select"
                >
                  <option value="">All</option>
                  <option value="active">Active</option>
                  <option value="blocked">Blocked</option>
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Created By
                <input
                  type="text"
                  name="userId"
                  onChange={(event) => setCreatedBy(event.target.value)}
                  className="custom-input"
                  placeholder="User id"
                />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Category
                <select
                  value={searchSetting.category}
                  name="category"
                  defaultValue={searchSetting.category}
                  onChange={(event) => {
                    setSearchSetting((prevState) => ({
                      ...prevState,
                      category: event.target.value,
                    }));
                  }}
                  className="custom-select"
                >
                  <option value="-1">All</option>
                  {categories.map((category, i) => (
                    <option key={i} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Type
                <select
                  value={searchSetting.type}
                  name="type"
                  defaultValue={searchSetting.type}
                  onChange={(event) => {
                    setSearchSetting((prevState) => ({
                      ...prevState,
                      type: event.target.value,
                    }));
                  }}
                  className="custom-select"
                >
                  <option value="-1">All</option>
                  {types.map((type, i) => (
                    <option key={i} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Sort by
                <select
                  id="model"
                  value={searchSetting.sortBy}
                  name="sortBy"
                  defaultValue={searchSetting.sortBy}
                  onChange={handleSearchSettingsChange}
                  className="custom-select"
                >
                  <option value="createdAt">Recently created</option>
                  <option value="updatedAt">Recently updated</option>
                  <option value="rating">Rating</option>
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="relative">
              <label className="relative text-[10px] text-gray-700">
                Order by
                <select
                  name="sortDirection"
                  value={searchSetting.sortDirection}
                  defaultValue={searchSetting.sortDirection}
                  onChange={handleSearchSettingsChange}
                  className="custom-select"
                >
                  <option value="DESC">Descending</option>
                  <option value="ASC">Ascending</option>
                </select>
                <IoIosArrowDown className="text-black absolute right-2.5 bottom-2.5 hover:cursor-pointer" />
              </label>
            </div>
            <div className="items-center flex gap-4 md:gap-6 pt-5">
              <div className="flex gap-2 items-center">
                <input
                  onChange={() =>
                    setSearchSetting({
                      ...searchSetting,
                      onlyWithReports: !searchSetting.onlyWithReports,
                    })
                  }
                  value={searchSetting.onlyWithReports}
                  defaultValue={searchSetting.onlyWithReports}
                  checked={searchSetting.onlyWithReports}
                  type="checkbox"
                  className="custom-checkbox"
                />
                <span className="text-sm">Only with claims</span>
              </div>
            </div>
            <div className="relative"></div>
            <div className="relative grid items-end justify-items-end">
              <button
                onClick={() => {
                  setSearchSetting(initialState);
                }}
                className="edit-btn"
              >
                Reset to defaults
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchPanel;
